import DOMPurify from 'dompurify';

/**
 * Entry and scene content is rich-text HTML from the realm file - the app's main XSS
 * risk, since script running here could read every decrypted realm (docs/WEB-PLAN.md
 * section 6). Everything shown from a realm goes through `sanitizeEntryHtml`, which keeps
 * formatting and links and drops anything active. Links: web and mail links open in a
 * new tab with no referrer; `realmery:entry/<id>` mention links (written by the desktop's
 * mention linking) stay, for the viewer to turn into in-app navigation; nothing else.
 */
const ALLOWED_URI = /^(?:https?:|mailto:|realmery:entry\/|#)/i;

// DOMPurify checks every attribute it does not know to be URI-safe against ALLOWED_URI,
// not only links, so plain values such as `face="Georgia"`, `size="5"` and
// `align="center"` would fail it. These attributes never hold a URL, so they skip it.
const NOT_URLS = [
  'face', 'size', 'align', 'valign', 'dir', 'lang', 'width', 'height', 'border',
  'colspan', 'rowspan', 'cellpadding', 'cellspacing', 'span', 'start', 'reversed', 'type',
  'abbr', 'scope', 'headers', 'nowrap', 'clear', 'hspace', 'vspace',
];

export const ENTRY_LINK_PREFIX = 'realmery:entry/';
/**
 * What mention links began with in realms written before the product's rename (docs/RENAME.md).
 * Read only here: every one becomes `realmery:entry/` as it's cleaned, so the old name is
 * never shown, exported or written.
 */
export const LEGACY_ENTRY_LINK = /^loreforge:entry\//i;

/** The entry id a mention link points at, or null for any other link. */
export function entryIdOfLink(href: string): string | null {
  const link = href.replace(LEGACY_ENTRY_LINK, ENTRY_LINK_PREFIX);
  return link.startsWith(ENTRY_LINK_PREFIX) && link.length > ENTRY_LINK_PREFIX.length ? link.slice(ENTRY_LINK_PREFIX.length) : null;
}

const FORBID_TAGS = [
  'style', 'link', 'meta', 'base', 'title',
  'form', 'input', 'button', 'textarea', 'select', 'option',
  'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'audio', 'video', 'source', 'track', 'picture', 'template', 'dialog',
];

// id and name could clobber DOM globals; srcset could pull images from anywhere.
const FORBID_ATTR = ['id', 'name', 'srcset', 'action', 'formaction', 'ping', 'background'];

// Old mention links become today's before DOMPurify checks them against ALLOWED_URI.
DOMPurify.addHook('beforeSanitizeAttributes', (node) => {
  if (node.nodeName !== 'A') return;
  const href = (node.getAttribute('href') ?? '').trim();
  if (LEGACY_ENTRY_LINK.test(href)) node.setAttribute('href', href.replace(LEGACY_ENTRY_LINK, ENTRY_LINK_PREFIX));
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName !== 'A') {
    return;
  }
  const href = node.getAttribute('href') ?? '';
  if (/^https?:/i.test(href)) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  } else {
    node.removeAttribute('target');
  }
});

/**
 * The same cleaning, returned as DOM nodes for the entry editor (editor/html.ts), so the
 * HTML is parsed only by DOMPurify's own Trusted Types policy and never written through
 * `innerHTML` by anything else.
 */
export function sanitizeEntryHtmlToFragment(html: string): DocumentFragment {
  return DOMPurify.sanitize(html, { ...CONFIG, RETURN_DOM_FRAGMENT: true });
}

export function sanitizeEntryHtml(html: string): TrustedHTML | string {
  return DOMPurify.sanitize(html, { ...CONFIG, RETURN_TRUSTED_TYPE: true });
}

const CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS,
  FORBID_ATTR,
  ALLOWED_URI_REGEXP: ALLOWED_URI,
  ADD_URI_SAFE_ATTR: NOT_URLS,
};
